//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr } from '@shared/SchemaTypes'
import * as React from 'react'
import { ReactElement, useEffect } from 'react'

import { RoleEnum } from '@shared/Enums'

import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import { Fab } from '@mui/material'
import { blue } from '@mui/material/colors'

import { useOtChainState } from '@react/redux/store'

import { uploadLogo, urlForFile } from '@react/apx/AwsApx'

export default function LogoUpload(props: {
  addrOrId: string | EthAddr
  size: number
  setHasIcon?: (value: boolean) => void
}): ReactElement {
  const addrOrId = props.addrOrId
  const size = props.size
  const [hasImage, setHasImage] = React.useState<boolean | undefined>(undefined)
  const { userConnection, stageConfiguration, cognitoUser } = useOtChainState()
  const stageName = stageConfiguration.stageName
  const restAPIKey = stageConfiguration.restAPIKey
  const { roleEnum } = userConnection ?? {}

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    if (file && addrOrId && cognitoUser) {
      const asyncFunction = async (): Promise<void> => {
        if (stageName && restAPIKey) {
          await uploadLogo(stageName, addrOrId, file, restAPIKey, cognitoUser)

          const url = urlForFile(stageName, `${addrOrId}/logo.png`)
          await fetch(url, { cache: 'reload', mode: 'no-cors' })
          document.body.querySelectorAll(`img[src='${url}']`).forEach((element: Element): void => {
            const img = element as HTMLImageElement
            if (img) {
              img.src = url
            }
          })
          setHasImage(true)
          if (props.setHasIcon) {
            props.setHasIcon(true)
          }
        }
      }
      asyncFunction().catch((e) => {
        console.error(e)
      })
    }
  }

  useEffect(() => {
    if (stageName && restAPIKey) {
      const url = urlForFile(stageName, `${addrOrId}/logo.png`)
      const img = new Image(50, 50)
      img.src = url
      if (img.complete) {
        setHasImage(true)
        if (props.setHasIcon) {
          props.setHasIcon(true)
        }
      }
      img.onload = () => {
        setHasImage(true)
        if (props.setHasIcon) {
          props.setHasIcon(true)
        }
      }
      img.onerror = () => {
        setHasImage(false)
        if (props.setHasIcon) {
          props.setHasIcon(false)
        }
      }
    }
  }, [addrOrId, stageName])
  if (hasImage && stageName) {
    return (
      <img
        style={{ width: size, height: size, margin: 'auto' }}
        src={urlForFile(stageName, `${addrOrId}/logo.png`)}
        alt={addrOrId}
      />
    )
  }
  if ((roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.PROTOCOL_MANAGER) && hasImage === false) {
    return (
      <>
        <input
          id="contained-button-file"
          style={{ display: 'none', width: size, height: size }}
          type="file"
          accept="image/png"
          onChange={handleLogoSelect}
        />
        <label htmlFor="contained-button-file">
          <Fab
            component="span"
            style={{
              color: blue[900],
              margin: 10,
            }}
          >
            <AddPhotoAlternateIcon />
          </Fab>
        </label>
      </>
    )
  }
  return <></>
}
